import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { ArrowRight, ChevronLeft, ChevronRight, FileText, User, CheckCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { useData } from "@/contexts/DataContext";
import CalendlyEmbed from "@/components/calendar/CalendlyEmbed";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { OptimizedBlogPostCard } from "@/components/blog/OptimizedBlogPostCard";
import { OptimizedImage } from "@/components/ui/image";
import ToolkitCard from "@/components/toolkit/ToolkitCard";

type BlogPost = {
  id: string;
  title: string;
  excerpt: string;
  image_url: string | null;
  category: string;
  created_at: string;
  text_direction: "ltr" | "rtl";
};

type Course = {
  id: string;
  title: string;
  description: string;
  level: string;
  price: string;
  status: string;
  image_url: string | null;
  enrolled?: boolean;
};

type Testimonial = {
  id: number;
  name: string;
  location: string;
  image: string;
  content: string;
};

type NavLink = {
  path: string;
  label: string;
  visible: boolean;
};

const Index = () => {
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [navigationLinks, setNavigationLinks] = useState<NavLink[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [courseLoading, setCourseLoading] = useState(true);
  const [testimonialsLoading, setTestimonialsLoading] = useState(true);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [testimonialsSortField, setTestimonialsSortField] = useState("display_order");
  const [testimonialsSortOrder, setTestimonialsSortOrder] = useState("asc");
  const testimonialRef = useRef<HTMLDivElement>(null);
  const { user, checkEnrollment } = useAuth();

  const isPathVisible = (path: string): boolean => {
    const link = navigationLinks.find(link => link.path === path);
    return link ? link.visible : true;
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const target = e.target as HTMLImageElement;
    target.onerror = null; // Prevent infinite callback loop
    target.src = "https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=687&q=80";
  };

  const formatGoogleDriveUrl = (url: string): string => {
    if (!url) return "https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=687&q=80";
    
    try {
      if (url.includes('drive.google.com')) {
        let fileId = '';
        
        const fileMatch = url.match(/\/file\/d\/([^\/]+)/);
        if (fileMatch && fileMatch[1]) {
          fileId = fileMatch[1];
        }
        
        const openMatch = url.match(/[?&]id=([^&]+)/);
        if (!fileId && openMatch && openMatch[1]) {
          fileId = openMatch[1];
        }
        
        const folderMatch = url.match(/\/folders\/([^\/]+)/);
        if (!fileId && folderMatch && folderMatch[1]) {
          return "https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=687&q=80";
        }
        
        if (fileId) {
          console.log("Using file ID:", fileId);
          return `https://drive.google.com/uc?export=view&id=${fileId}`;
        }
      }
    } catch (error) {
      console.error("Error formatting Google Drive URL:", error);
    }
    
    return url;
  };

  const nextTestimonial = () => {
    setActiveTestimonial(prev => prev === testimonials.length - 1 ? 0 : prev + 1);
  };

  const prevTestimonial = () => {
    setActiveTestimonial(prev => prev === 0 ? testimonials.length - 1 : prev - 1);
  };

  useEffect(() => {
    const fetchNavigationLinks = async () => {
      try {
        const { data, error } = await supabase
          .from('navigation_links')
          .select('path, label, visible')
          .order('order', { ascending: true });
        
        if (error) {
          console.error('Error fetching navigation links:', error);
          return;
        }
        
        setNavigationLinks(data || []);
      } catch (error) {
        console.error('Error in fetchNavigationLinks:', error);
      }
    };

    const fetchBlogPosts = async () => {
      try {
        setIsLoading(true);
        const {
          data,
          error
        } = await supabase.from('blog_posts').select('id, title, excerpt, image_url, category, created_at, text_direction').eq('published', true).order('created_at', {
          ascending: false
        }).limit(3);
        if (error) {
          console.error('Error fetching blog posts:', error);
          return;
        }
        
        const typedData = data?.map(post => ({
          ...post,
          text_direction: (post.text_direction as "ltr" | "rtl") || "ltr"
        }));
        
        setBlogPosts(typedData || []);
      } catch (error) {
        console.error('Error in fetchBlogPosts:', error);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchTestimonials = async () => {
      try {
        setTestimonialsLoading(true);
        const { data, error } = await supabase
          .from('testimonials')
          .select('*')
          .order(testimonialsSortField, { ascending: testimonialsSortOrder === 'asc' });
        
        if (error) {
          console.error('Error fetching testimonials:', error);
          return;
        }
        
        setTestimonials(data || []);
        setActiveTestimonial(0);
      } catch (error) {
        console.error('Error in fetchTestimonials:', error);
      } finally {
        setTestimonialsLoading(false);
      }
    };

    const fetchCourses = async () => {
      try {
        setCourseLoading(true);
        const {
          data,
          error
        } = await supabase.from('courses').select('id, title, description, level, price, status, image_url').order('created_at', {
          ascending: false
        }).limit(3);
        if (error) {
          console.error('Error fetching courses:', error);
          return;
        }

        if (user) {
          const coursesWithEnrollmentStatus = await Promise.all(
            (data || []).map(async (course) => {
              const isEnrolled = await checkEnrollment(course.id);
              return { ...course, enrolled: isEnrolled };
            })
          );
          setCourses(coursesWithEnrollmentStatus);
        } else {
          setCourses(data || []);
        }
      } catch (error) {
        console.error('Error in fetchCourses:', error);
      } finally {
        setCourseLoading(false);
      }
    };

    fetchNavigationLinks();
    fetchBlogPosts();
    fetchCourses();
    fetchTestimonials();
  }, [testimonialsSortField, testimonialsSortOrder, user, checkEnrollment]);

  return <Layout 
    title="Freelance With Andy - Turning Technical Challenges into Digital Products"
    description="Andy Nady turns complex IoT and AI challenges into simple, scalable digital products that drive real value and business impact."
    keywords="freelance, web development, projects, product, andy, andrew, courses, digital, IoT, AI"
    path="/"
  >
      <section className="bg-gradient-to-br from-green-50 to-gray-100 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-12">
            <div className="max-w-2xl">
              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                The go-to resource for freelancers with 
                <span className="text-[#34BF49]"> expert insights and actionable strategies for success.</span>
              </h1>
              <div className="flex flex-wrap gap-4 mt-8">
                <CalendlyEmbed 
                  buttonText="Get Your Free Call" 
                  buttonClassName="text-md px-6 h-12"
                />
                <Link to="/blog">
                  <Button variant="outline" className="text-md px-6 h-12">
                    View My Blogs
                  </Button>
                </Link>
              </div>
            </div>
            <div className="w-full md:w-1/3">
              <div className="rounded-lg overflow-hidden shadow-xl bg-white p-2 border border-brand-green/10">
                <img
                  alt="Andrew Nady" 
                  title="Top Rated-Plus freelancer as a Technical Product Manager on Upwork"
                  className="w-full h-auto rounded" 
                  src="/lovable-uploads/ba4f0776-af2f-4f44-a112-8b601bcf43b8.png"
                  width={387}
                  height={391}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-4">
            <span className="text-[#34BF49]">Proven Track Record on Upwork</span>
          </h2>
          <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
            Delivering consistent quality and results that drive success for clients around the world.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center p-6 bg-gray-50 rounded-lg shadow-sm">
              <p className="text-4xl font-bold text-brand-green mb-2">+15</p>
              <p className="text-gray-600">Projects Completed</p>
            </div>
            <div className="text-center p-6 bg-gray-50 rounded-lg shadow-sm">
              <p className="text-4xl font-bold text-brand-green mb-2">+3,600</p>
              <p className="text-gray-600">Hours Worked</p>
            </div>
            <div className="text-center p-6 bg-gray-50 rounded-lg shadow-sm">
              <p className="text-4xl font-bold text-brand-green mb-2">+16</p>
              <p className="text-gray-600">Happy Clients</p>
            </div>
            <div className="text-center p-6 bg-gray-50 rounded-lg shadow-sm">
              <p className="text-4xl font-bold text-brand-green mb-2">100%</p>
              <p className="text-gray-600">Job Success Rate</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-4">
            <span className="text-[#34BF49]">Client Testimonials</span>
          </h2>
          <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
            Read what clients have to say about working with me.
          </p>
          
          <div className="hidden">
            <Select
              value={testimonialsSortField}
              onValueChange={setTestimonialsSortField}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="display_order">Custom Order</SelectItem>
                <SelectItem value="name">Name</SelectItem>
                <SelectItem value="created_at">Date</SelectItem>
                <SelectItem value="location">Location</SelectItem>
              </SelectContent>
            </Select>
            
            <Select
              value={testimonialsSortOrder}
              onValueChange={setTestimonialsSortOrder}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="asc">Ascending</SelectItem>
                <SelectItem value="desc">Descending</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="relative" ref={testimonialRef}>
            {testimonialsLoading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-green"></div>
              </div>
            ) : testimonials.length > 0 ? (
              <div className="overflow-hidden">
                <div className="flex justify-center">
                  <div className="bg-white p-6 rounded-lg shadow-sm max-w-2xl">
                     <div className="flex items-center mb-4">
                       <div className="w-12 h-12 rounded-full overflow-hidden mr-4">
                         <OptimizedImage 
                           src={formatGoogleDriveUrl(testimonials[activeTestimonial]?.image || '')} 
                           alt={`${testimonials[activeTestimonial]?.name || 'Client'}`} 
                           title={`${testimonials[activeTestimonial]?.name} - Client from ${testimonials[activeTestimonial]?.location}`}
                           className="w-full h-full object-cover" 
                           width={48}
                           height={48}
                           priority={false}
                         />
                       </div>
                      <div>
                        <h3 className="font-semibold">{testimonials[activeTestimonial]?.name}</h3>
                        <p className="text-sm text-gray-500">{testimonials[activeTestimonial]?.location}</p>
                        <div className="flex text-yellow-400">
                          <span>★★★★★</span>
                        </div>
                      </div>
                    </div>
                    <p className="text-gray-600">
                      "{testimonials[activeTestimonial]?.content}"
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">No testimonials available yet.</p>
              </div>
            )}
            
            {testimonials.length > 0 && (
              <>
                <div className="flex justify-center mt-6 gap-2">
                  <Button variant="outline" size="icon" className="rounded-full" onClick={prevTestimonial} aria-label="Previous testimonial">
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  
                  <div className="flex space-x-2 items-center">
                    {testimonials.map((_, index) => (
                      <button 
                        key={index} 
                        className={`p-2 rounded-full transition-all`}
                        onClick={() => setActiveTestimonial(index)} 
                        aria-label={`Go to testimonial ${index + 1}`}
                      >
                        <div className={`h-2 rounded-full transition-all ${activeTestimonial === index ? "w-6 bg-brand-green" : "w-2 bg-gray-300"}`} />
                      </button>
                    ))}
                  </div>
                  
                  <Button variant="outline" size="icon" className="rounded-full" onClick={nextTestimonial} aria-label="Next testimonial">
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-4">
            <span className="text-[#34BF49]">Freelance Toolkit</span>
          </h2>
          <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
            Powerful AI-powered tools designed to help freelancers create winning proposals, professional profiles, and stand out in the marketplace.
          </p>
          <div className="grid md:grid-cols-2 gap-8">
            <ToolkitCard
              title="Upwork Proposal Generator"
              description="Create winning Upwork proposals in seconds with our AI-powered proposal generator."
              linkTo="/freelance-toolkit/proposal-generator"
              buttonText="Generate Proposal"
              imageSrc="/lovable-uploads/3246f7b7-56b0-4868-b718-522e22deb0c7.png"
            />

            <ToolkitCard
              title="Profile Overview Generator"
              description="Create a compelling professional profile for your freelance platforms with AI assistance."
              linkTo="/freelance-toolkit/profile-generator"
              buttonText="Generate Profile Overview"
              imageSrc="/lovable-uploads/16ea837e-74c3-4eef-b1f8-51b632548d6a.png"
            />
          </div>
          <div className="text-center mt-10">
            <Link to="/freelance-toolkit">
              <Button variant="outline" className="gap-2">
                View All Tools <ArrowRight size={16} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {isPathVisible('/courses') && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-4">
              <span className="text-[#34BF49]">Featured Courses</span>
            </h2>
            <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
              Level up your freelancing skills with comprehensive courses on technical product management, IoT development, and digital transformation.
            </p>
            
            {courseLoading ? <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-green"></div>
              </div> : courses.length > 0 ? <div className="grid md:grid-cols-3 gap-8">
                {courses.map(course => <div key={course.id} className="bg-white rounded-lg overflow-hidden shadow-sm flex flex-col h-full">
                    <div className="h-48 bg-gray-100">
                      <OptimizedImage 
                        src={course.image_url || "/placeholder.svg"} 
                        alt={course.title} 
                        title={`Course: ${course.title} - ${course.level} Level - ${course.price}`}
                        className="w-full h-full object-cover" 
                        width={384}
                        height={192}
                        priority={false}
                      />
                    </div>
                    <div className="p-6 flex-grow">
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex gap-2">
                          <Badge variant={course.status === "Enrolling" ? "default" : "secondary"}>
                            {course.status}
                          </Badge>
                          {course.enrolled && (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                              Enrolled
                            </Badge>
                          )}
                        </div>
                        <span className="font-bold text-lg">{course.price}</span>
                      </div>
                      <h3 className="text-xl font-bold mb-4">{course.title}</h3>
                      <p className="text-sm text-gray-500">
                        <span className="font-medium">Level:</span> {course.level}
                      </p>
                    </div>
                    <div className="px-6 pb-6">
                      {course.enrolled ? (
                        <Link to={`/courses/${course.id}/lessons`} className="w-full">
                          <Button className="w-full">Continue Learning</Button>
                        </Link>
                      ) : (
                        <Link to={`/courses/${course.id}`} className="w-full">
                          <Button 
                            className="w-full" 
                            variant="default"
                          >
                            {course.status === "Enrolling" ? "Enroll Now" : "Get Notified"}
                          </Button>
                        </Link>
                      )}
                    </div>
                  </div>)}
              </div> : <div className="text-center py-8">
                <p className="text-gray-500">No courses available yet.</p>
              </div>}
          
            <div className="text-center mt-10">
              <Link to="/courses">
                <Button variant="outline" className="gap-2">
                  View All Courses <ArrowRight size={16} />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-4">
            <span className="text-[#34BF49]">Recent Blog Posts</span>
          </h2>
          <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
            Expert insights and practical advice on freelancing, product management, and navigating the digital landscape successfully.
          </p>
          
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-green"></div>
            </div>
          ) : blogPosts.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-8">
              {blogPosts.map(post => (
                <OptimizedBlogPostCard
                  key={post.id}
                  id={post.id}
                  title={post.title}
                  excerpt={post.excerpt}
                  image_url={post.image_url}
                  category={post.category}
                  text_direction={post.text_direction}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">No blog posts available yet.</p>
            </div>
          )}
          
          <div className="text-center mt-10">
            <Link to="/blog">
              <Button variant="outline" className="gap-2">
                View All Posts <ArrowRight size={16} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 bg-brand-green text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Elevate Your Product?</h2>
          <p className="text-lg mb-8 max-w-3xl mx-auto">
            Let's work together to transform your vision into a successful digital product that drives real business results.
          </p>
          <CalendlyEmbed 
            buttonText="Start a Conversation" 
            buttonClassName="bg-white text-brand-green hover:bg-gray-100 px-8 py-6 text-lg h-12"
          />
        </div>
      </section>
    </Layout>;
};

export default Index;
